/**
 * Shared (isomorphic) form helpers for the label editor.
 *
 * The editor's <form> field names and the pure mapping from raw field values → an engine
 * `CalcInput` live here so BOTH the client component (live preview) and the server route action
 * (persist + validate) use the exact same parsing — the client preview can never disagree with
 * what the server validates/saves. No React, no Shopify, no Node imports: safe on both sides.
 */
import {
  US_NF_NUTRIENTS,
  type CalcInput,
  type NutrientId,
  type NutrientInput,
} from "@easynutrifact/label-engine";

/** Field names for the serving block. Nutrient fields are keyed `nutrient:<id>`. */
export const SERVING_FIELDS = {
  householdMeasure: "household_measure",
  metricGrams: "metric_grams",
  servingsPerContainer: "servings_per_container",
} as const;

/** The form-field name for a nutrient amount input. */
export function nutrientFieldName(id: NutrientId): string {
  return `nutrient:${id}`;
}

/** A flat string→string record of the form's current values. */
export type FieldValues = Record<string, string>;

/**
 * Build a `CalcInput` from raw field values. Blank nutrient fields are OMITTED (absent ≠ 0 — the
 * engine reports missing mandatory nutrients). Non-numeric/blank serving numbers degrade to
 * 0 / "" so the engine's validator surfaces them softly rather than throwing.
 */
export function valuesToCalcInput(values: FieldValues): CalcInput {
  const nutrients: NutrientInput[] = [];
  for (const def of US_NF_NUTRIENTS) {
    const raw = values[nutrientFieldName(def.id)];
    if (raw == null || String(raw).trim() === "") continue;
    const amount = Number(raw);
    if (!Number.isFinite(amount)) continue;
    nutrients.push({ id: def.id, amount, unit: def.unit });
  }

  const metricGrams = Number(values[SERVING_FIELDS.metricGrams]);
  const spcRaw = (values[SERVING_FIELDS.servingsPerContainer] ?? "").trim();
  const spcNum = Number(spcRaw);
  const servingsPerContainer: number | "varied" =
    spcRaw.toLowerCase() === "varied"
      ? "varied"
      : Number.isFinite(spcNum) && spcRaw !== ""
        ? spcNum
        : 0;

  return {
    market: "US",
    profile: "adult",
    serving: {
      householdMeasure: values[SERVING_FIELDS.householdMeasure] ?? "",
      metricGrams: Number.isFinite(metricGrams) ? metricGrams : 0,
      servingsPerContainer,
    },
    nutrients,
  };
}

/** Has the merchant entered enough to render a meaningful panel? (calories present.) */
export function hasEnoughForPreview(values: FieldValues): boolean {
  const cal = values[nutrientFieldName("energy")];
  return cal != null && String(cal).trim() !== "" && Number.isFinite(Number(cal));
}

/** Turn a saved `CalcInput`-shaped doc back into flat field values for prefill. */
export function calcInputToValues(input: CalcInput): FieldValues {
  const values: FieldValues = {
    [SERVING_FIELDS.householdMeasure]: input.serving.householdMeasure ?? "",
    [SERVING_FIELDS.metricGrams]:
      input.serving.metricGrams != null ? String(input.serving.metricGrams) : "",
    [SERVING_FIELDS.servingsPerContainer]: String(input.serving.servingsPerContainer ?? ""),
  };
  for (const n of input.nutrients) {
    values[nutrientFieldName(n.id)] = String(n.amount);
  }
  return values;
}

/**
 * The FDA's published Nutrition Facts example (the 2/3-cup granola sample from the 2016 final
 * rule) as editor field values — powers the "Try with sample data" button so a merchant can
 * see a complete, correct panel instantly without hunting for numbers.
 */
export function sampleFieldValues(): FieldValues {
  return {
    [SERVING_FIELDS.householdMeasure]: "2/3 cup",
    [SERVING_FIELDS.metricGrams]: "55",
    [SERVING_FIELDS.servingsPerContainer]: "8",
    [nutrientFieldName("energy")]: "230",
    [nutrientFieldName("fat_total")]: "8",
    [nutrientFieldName("fat_sat")]: "1",
    [nutrientFieldName("fat_trans")]: "0",
    [nutrientFieldName("cholesterol")]: "0",
    [nutrientFieldName("sodium")]: "160",
    [nutrientFieldName("carb_total")]: "37",
    [nutrientFieldName("fiber")]: "4",
    [nutrientFieldName("sugars_total")]: "12",
    [nutrientFieldName("sugars_added")]: "10",
    [nutrientFieldName("protein")]: "3",
    [nutrientFieldName("vitamin_d")]: "2",
    [nutrientFieldName("calcium")]: "260",
    [nutrientFieldName("iron")]: "8",
    [nutrientFieldName("potassium")]: "235",
  };
}

/** True when the merchant hasn't entered anything yet (all fields blank/whitespace). */
export function isEmptyForm(values: FieldValues): boolean {
  return Object.values(values).every((v) => v == null || String(v).trim() === "");
}

/** Read a FormData (the posted editor form) into a flat FieldValues record. */
export function formDataToValues(form: FormData): FieldValues {
  const values: FieldValues = {};
  for (const [key, value] of form.entries()) {
    if (typeof value === "string") values[key] = value;
  }
  return values;
}
